import React from 'react';
import { upperCase } from 'lodash';
import { v4 as generateUuid } from 'uuid';
import { useQuery } from 'apollo-client-ppc';
import Button, { LoadingButton } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { CheckboxField, Field } from '@atlaskit/form';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Panel from '@atlaskit/panel';
import ToolTip from '@atlaskit/tooltip';
import Lozenge from '@atlaskit/lozenge';
import {
  CommerceAddress,
  CreditCard,
  EntitlementDetails,
  FetchAvailableInvoiceGroupsForOrderReviewQuery,
  FetchAvailableInvoiceGroupsForOrderReviewQueryVariables,
  FetchBillToAddressByTransactionAccountIdQuery,
  FetchBillToAddressByTransactionAccountIdQueryVariables,
  PaymentMethodType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { useConsentHub } from '@css-xp/ppc-consent-hub';
import { PartnerAccount } from '@css-xp/ppc-types';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { PaymentMethod, PaymentMethodId } from '@atlassian/commerce-payment-methods';
import { generatedTypesNew } from '@css-xp/graphtypes';
import {
  DISCOUNT_LABELS,
  Divider,
  EntitlementIcon,
  useApplicationContext,
  useFeatureToggle,
  useModalDialog,
} from '@css-xp/base';
import { getChargeElement } from '@css-xp/ppc-utils';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { OrderResponseDto } from '@atlassian/commerce-ccp-gateway-client';
import { FormatCommerceAddress, tableErrorMessage } from '@css-xp/ppc-common';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { AddContactFields } from '@css-xp/ppc-add-contact-fields';
import { getPartnerServiceDeskUrlForGenericHelp, trackPartnerServiceDeskClick } from '@css-xp/ppc-navigation';
import { usePartnerCatalogueContext } from './context/partner-catalogue-context';
import { Estimate } from './estimate';
import { useOrderEstimatePDFDownload } from './hooks/use-estimate-pdf-download';
import { usePartnerPromotionDetails } from './hooks/use-partner-promotion-details';
import { PlaceOrderParams, usePlaceOrder } from './hooks/use-place-order';
import { CheckoutOperation, usePartnerCheckoutNotification } from './hooks/use-place-order-notification';
import { useProcessLineItemsForOrderIntentEstimate } from './hooks/use-process-line-items-for-order-intent-estimate';
import { OrderConfirmationStep } from './order-confirmation';
import { fetchAvailableInvoiceGroupsForOrderReview } from './queries/fetch-available-invoice-groups';
import { fetchBillToAddressByTransactionAccountId } from './queries/fetch-bill-to-address';
import { SearchSoldToAddress } from './search-sold-to-address';
import { SelectPaymentMethod } from './select-payment-method';
import { PartnerQuoteLineItem, PartnerQuoteLineItems, ActualEstimates, SiteDetails } from './types';
import { getInvoiceGroup } from './utils';
import { useEstimateJsonDownload } from './hooks/use-estimate-json-download';
import { AddExistingSite } from './add-existing-site';
import './order-review-with-provisioning.scss';

export type OrderReviewWithProvisioningProps = {
  lineItems: PartnerQuoteLineItems;
  transactionAccountId: string;
  applicablePromotionId: string | null | undefined;
  onClose?: () => void;
};

type ParentEntitlementWithAppLineItems = {
  entitlementDetails: EntitlementDetails;
  appsLineItems: PartnerQuoteLineItems;
};

const legalLinks = {
  termsOfUseUrl: 'https://www.atlassian.com/legal/customer-agreement',
  privacyPolicyUrl: 'https://www.atlassian.com/legal/privacy-policy',
};

const formatIfCreditCard = (paymentMethod: any): PaymentMethod => {
  if (paymentMethod.type !== PaymentMethodType.CARD) {
    return paymentMethod;
  }
  const creditCard = paymentMethod as CreditCard;

  // convert expMonth & expYear to Numbers
  const expMonth = +(creditCard.card?.expirationMonth ?? 0);
  const expYear = +(creditCard.card?.expirationYear ?? 0);
  const card = {
    brand: creditCard.card?.brand ?? '',
    expMonth,
    expYear,
    last4: creditCard.card?.last4Numbers ?? '',
    name: creditCard.card?.cardHolderName ?? '',
  };

  const cardDetails: PaymentMethod = {
    id: creditCard.id as PaymentMethodId,
    type: paymentMethod.type,
    card,
    default: paymentMethod.default,
  };

  return cardDetails;
};

const renderTaxBreakUp = (taxBreakUp: { taxIdLabel: string; tax: number }[]) => {
  return (
    <>
      {taxBreakUp &&
        taxBreakUp.map((breakUp, idx) => {
          return (
            <div className="row-flex-container" key={`tax-breakup-${idx}`}>
              <p className="selected-app-pricing-details"></p>
              <p className="selected-app-pricing-details">{`${breakUp.taxIdLabel} : USD ${
                Math.round(breakUp.tax * 100) / 100
              }`}</p>
            </div>
          );
        })}
    </>
  );
};

const ProductTrialInfo = ({ trialLength, isApp = false }: { trialLength?: string | null; isApp?: boolean }) => {
  if (trialLength) {
    const lozengeText = `${trialLength} DAY TRIAL`;
    if (isApp) {
      return (
        <div className="row-flex-container">
          <p className="selected-app-pricing-details">
            <Lozenge appearance={'inprogress'} testId="order-lineitem-trial-info">
              {lozengeText}
            </Lozenge>
          </p>
        </div>
      );
    } else {
      return (
        <div className="row-flex-container">
          <Lozenge appearance={'inprogress'} testId="order-lineitem-trial-info">
            {lozengeText}
          </Lozenge>
        </div>
      );
    }
  }

  return <></>;
};

const renderAppsWithParentEntitlementLineItems = ({
  lineItems,
  actualEstimates,
  loading,
  productTrialMap,
  getAppsParentEntitlement,
}: {
  lineItems: PartnerQuoteLineItems;
  actualEstimates: ActualEstimates;
  adminTaxBreakUp: Map<string, { adminTaxItemLabel: string; totalAdminTax: number }>;
  loading: boolean;
  productTrialMap: Map<string, string | null>;
  getAppsParentEntitlement: (productId: string) => EntitlementDetails | undefined;
}) => {
  const parentEntitlementLineItems: any = {};
  if (lineItems?.length) {
    for (const item of lineItems) {
      const parentEntitlement = getAppsParentEntitlement(item?.productKey);
      const entitlementNumber = parentEntitlement?.entitlementNumber;
      if (entitlementNumber) {
        if (parentEntitlementLineItems[entitlementNumber]) {
          // grouping all entitlement apps into single entitlement object
          parentEntitlementLineItems[entitlementNumber] = {
            ...parentEntitlementLineItems[entitlementNumber],
            appsLineItems: [...parentEntitlementLineItems[entitlementNumber].appsLineItems, item],
          };
        } else {
          parentEntitlementLineItems[entitlementNumber] = {
            entitlementDetails: parentEntitlement,
            appsLineItems: [item],
          };
        }
      }
    }
  }

  const parentEntitlementWithAppsLineItems: ParentEntitlementWithAppLineItems[] = [];
  // converting parentEntitlementLineItems into array object
  for (const entitlementNumber in parentEntitlementLineItems) {
    if (parentEntitlementLineItems[entitlementNumber]) {
      parentEntitlementWithAppsLineItems.push(parentEntitlementLineItems[entitlementNumber]);
    }
  }

  return parentEntitlementWithAppsLineItems.map((lineItem: ParentEntitlementWithAppLineItems) => {
    const parentEntitlement = lineItem?.entitlementDetails;
    const integratedAppsWithEntitlement = lineItem?.appsLineItems;
    return (
      <>
        <div className="line-item-app-container" key={`${parentEntitlement?.productId}`}>
          <div className="line-item-app-icon">
            <EntitlementIcon productId={parentEntitlement?.productId as string} className="avatar large" isCloud />
          </div>
          <div className="selected-app">
            <div className="row-flex-container">
              <div className="existing-site-container">
                <h4 className="selected-app-name" data-testId={`parent-product-name-${parentEntitlement.id}`}>
                  {parentEntitlement?.productName}
                </h4>
              </div>
              <Lozenge appearance={'default'}>{upperCase('Existing product')}</Lozenge>
            </div>
            {parentEntitlement?.cloudSiteHostname && (
              <div
                className="row-flex-container existing-site-container"
                data-testId={`parent-product-cloud-site-url-${parentEntitlement.id}`}
              >
                <small>{parentEntitlement?.cloudSiteHostname}</small>
              </div>
            )}
            <div className="row-flex-container">
              <p className="selected-app-pricing-details">Entitlement Number </p>
              <p className="selected-app-pricing-details">{parentEntitlement?.entitlementNumber}</p>
            </div>
            <div className="row-flex-container">
              <p className="selected-app-pricing-details">Active users </p>
              <p className="selected-app-pricing-details" data-testId={`parent-product-users-${parentEntitlement.id}`}>
                {parentEntitlement?.unitCount}
              </p>
            </div>
            <div className="row-flex-container">
              <p className="selected-app-pricing-details" data-testId={`parent-product-plan-${parentEntitlement.id}`}>
                {`Plan: ${parentEntitlement?.edition}`}
                {parentEntitlement?.billingFrequency ? ', ' + parentEntitlement?.billingFrequency : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="integrated-apps-container">
          <Panel
            header={
              <div
                className="line-item-app-container"
                data-testid={`integrated-apps-panel-${parentEntitlement.id}`}
                key={`integrated-apps-${parentEntitlement.id}`}
              >
                <div className="line-item-app-icon">
                  <EntitlementIcon productId={'integrated-apps'} className="avatar large" isCloud />
                </div>
                <div className="selected-app">
                  <div className="row-flex-container">
                    <h4 className="selected-app-name">Integrated Apps</h4>
                  </div>
                </div>
              </div>
            }
          >
            {integratedAppsWithEntitlement?.map((appLineItem: PartnerQuoteLineItem) => {
              return (
                <div
                  className="integrated-apps-line-items-container"
                  key={`integrated-apps-${appLineItem?.lineItemId}`}
                >
                  <div className="line-item-app-container">
                    <div className="line-item-app-icon">
                      <EntitlementIcon
                        logoId={appLineItem?.logoId}
                        productId={appLineItem?.productKey}
                        className="avatar medium pt-4"
                        isCloud
                      />
                    </div>
                    <div className="selected-app">
                      <div className="row-flex-container">
                        <h4
                          className="selected-app-name integrated-app"
                          data-testid={`integrated-apps-${appLineItem?.lineItemId}`}
                        >
                          {appLineItem?.name}
                        </h4>
                        <Estimate
                          currency={'USD'}
                          price={actualEstimates.get(appLineItem?.lineItemId)?.grandTotal ?? 0}
                          loading={loading}
                        />
                      </div>
                      <ProductTrialInfo trialLength={productTrialMap.get(appLineItem?.productKey)} isApp={true} />
                      <div className="row-flex-container">
                        <p className="selected-app-pricing-details">
                          {`Plan: ${appLineItem?.offeringName}`}
                          {!appLineItem?.isFreeProduct ? ', ' + appLineItem?.billingCycleLabel : ''}
                        </p>
                      </div>
                      <div className="row-flex-container">
                        <p className="selected-app-pricing-details">
                          Estimated {getChargeElement({ productId: appLineItem?.productKey, labelPlural: true })}{' '}
                        </p>
                        <p className="selected-app-pricing-details">{appLineItem?.estimatedNumberOfUsers}</p>
                      </div>
                      <div className="row-flex-container">
                        <p className="selected-app-pricing-details">List price </p>
                        <Estimate
                          currency={'USD'}
                          price={actualEstimates.get(appLineItem?.lineItemId)?.listPrice ?? 0}
                          loading={loading}
                        />
                      </div>
                      {actualEstimates.get(appLineItem?.lineItemId)?.allPromotionsApplied &&
                        actualEstimates.get(appLineItem?.lineItemId)?.allPromotionsApplied.map((promotionInfo) => {
                          return (
                            <div
                              className="row-flex-container"
                              key={`${appLineItem?.lineItemId}-${promotionInfo.adjustmentLabel}`}
                            >
                              <p className="selected-app-pricing-details">{`${promotionInfo.adjustmentLabel}: ${promotionInfo.percent}%`}</p>
                              <Estimate
                                currency={'USD'}
                                price={-(promotionInfo.totalAdjustment ?? 0)}
                                loading={loading}
                                className={'sub-title-pricing-currency'}
                              />
                            </div>
                          );
                        })}
                      <div className="row-flex-container">
                        <h4 className="estimate-partner-discount">Estimate incl. discounts</h4>
                        <Estimate
                          currency={'USD'}
                          price={actualEstimates.get(appLineItem?.lineItemId)?.estimateIncludingPartnerDiscount ?? 0}
                          loading={loading}
                          className={'estimate-partner-discount-currency'}
                        />
                      </div>
                      {actualEstimates.get(appLineItem.lineItemId)?.isAdminFeeApplicable && (
                        <div className="row-flex-container">
                          <p className="selected-app-pricing-details">{`Administration fee (DST)`}</p>
                          <Estimate
                            currency={'USD'}
                            price={actualEstimates.get(appLineItem?.lineItemId)?.adminFee ?? 0}
                            loading={loading}
                            className={'sub-title-pricing-currency'}
                          />
                        </div>
                      )}

                      {actualEstimates
                        .get(appLineItem?.lineItemId)
                        ?.itemLevelTaxInfo?.map(([taxLabel, { tax, taxPercent }]) => {
                          return (
                            <div key={`tax-(${taxPercent}-${taxLabel})`} className="row-flex-container">
                              <p className="selected-app-pricing-details">{`Tax (${taxPercent}% ${taxLabel})`}</p>
                              <Estimate
                                currency={'USD'}
                                price={tax}
                                loading={loading}
                                className={'sub-title-pricing-currency'}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              );
            })}
          </Panel>
        </div>
      </>
    );
  });
};

const renderLineItems = ({
  lineItems,
  isJiraFamily,
  actualEstimates,
  actualEstimatesAvailable,
  loading,
  existingCloudSite,
  productTrialMap,
}: {
  lineItems: PartnerQuoteLineItems;
  isJiraFamily: boolean;
  actualEstimates: ActualEstimates;
  actualEstimatesAvailable: boolean;
  adminTaxBreakUp: Map<string, { adminTaxItemLabel: string; totalAdminTax: number }>;
  loading: boolean;
  existingCloudSite: string | undefined;
  productTrialMap: Map<string, string | null>;
}) => {
  return lineItems.map((lineItem, index) => {
    return (
      <>
        {index !== 0 && !isJiraFamily && <Divider />}
        <div className="line-item-app-container" key={`${lineItem.lineItemId}`}>
          <div className="line-item-app-icon">
            <EntitlementIcon productId={lineItem?.productKey} className="avatar medium pt-4" isCloud />
          </div>
          <div className="selected-app">
            <div className="row-flex-container">
              <div className="existing-site-container">
                <h4 className="selected-app-name" data-testid="selected-app-name">
                  {lineItem?.name}
                </h4>
              </div>
              <Estimate
                currency={'USD'}
                price={actualEstimates.get(lineItem?.lineItemId)?.grandTotal ?? lineItem?.estimatedPrice ?? 0}
                loading={loading}
              />
            </div>
            <ProductTrialInfo trialLength={productTrialMap.get(lineItem?.productKey)} />
            {isJiraFamily && existingCloudSite && (
              <div className="row-flex-container existing-site-container">
                <small>{existingCloudSite}</small>
              </div>
            )}
            <div className="row-flex-container">
              <p className="selected-app-pricing-details">
                {`Plan: ${lineItem?.offeringName}`}
                {!lineItem?.isFreeProduct ? ', ' + lineItem?.billingCycleLabel : ''}
              </p>
            </div>
            {!lineItem.isFreeProduct && (
              <div className="row-flex-container">
                <p className="selected-app-pricing-details">
                  Estimated {getChargeElement({ productId: lineItem?.productKey, labelPlural: true })}{' '}
                </p>
                <p className="selected-app-pricing-details">{lineItem?.estimatedNumberOfUsers}</p>
              </div>
            )}
            {actualEstimatesAvailable && (
              <>
                <div className="row-flex-container">
                  <p className="selected-app-pricing-details">List price</p>
                  <Estimate
                    currency={'USD'}
                    price={actualEstimates.get(lineItem?.lineItemId)?.listPrice ?? lineItem?.estimatedPrice ?? 0}
                    loading={loading}
                    className={'sub-title-pricing-currency'}
                  />
                </div>
                {actualEstimates.get(lineItem?.lineItemId)?.discountPercent && (
                  <div className="row-flex-container">
                    <p className="selected-app-pricing-details">{`Partner discount: ${
                      actualEstimates.get(lineItem?.lineItemId)?.discountPercent
                    }%`}</p>
                    <Estimate
                      currency={'USD'}
                      price={-(actualEstimates.get(lineItem.lineItemId)?.totalDiscount ?? 0)}
                      loading={loading}
                      className={'sub-title-pricing-currency'}
                    />
                  </div>
                )}

                <div className="row-flex-container">
                  <h4 className="estimate-partner-discount">Estimate incl. partner discount</h4>
                  <Estimate
                    currency={'USD'}
                    price={actualEstimates.get(lineItem?.lineItemId)?.estimateIncludingPartnerDiscount ?? 0}
                    loading={loading}
                    className={'estimate-partner-discount-currency'}
                  />
                </div>
                {actualEstimates.get(lineItem.lineItemId)?.isAdminFeeApplicable && (
                  <div className="row-flex-container">
                    <p className="selected-app-pricing-details">{`Administration fee (DST)`}</p>
                    <Estimate
                      currency={'USD'}
                      price={actualEstimates.get(lineItem?.lineItemId)?.adminFee ?? 0}
                      loading={loading}
                      className={'sub-title-pricing-currency'}
                    />
                  </div>
                )}

                {actualEstimates.get(lineItem?.lineItemId)?.itemLevelTaxInfo?.map(([taxLabel, { tax, taxPercent }]) => {
                  return (
                    <div key={`tax-(${taxPercent}-${taxLabel})`} className="row-flex-container">
                      <p className="selected-app-pricing-details">{`Tax (${taxPercent}% ${taxLabel})`}</p>
                      <Estimate
                        currency={'USD'}
                        price={tax}
                        loading={loading}
                        className={'sub-title-pricing-currency'}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
        {lineItem?.appLineItems && lineItem?.appLineItems.length > 0 && (
          <div className="integrated-apps-container">
            <Panel
              header={
                <div className="line-item-app-container" key={`integrated-apps-${lineItem.lineItemId}`}>
                  <div className="line-item-app-icon">
                    <EntitlementIcon productId={'integrated-apps'} className="avatar large" isCloud />
                  </div>
                  <div className="selected-app">
                    <div className="row-flex-container">
                      <h4 className="selected-app-name">Integrated Apps</h4>
                      <Estimate
                        currency={'USD'}
                        price={actualEstimates.get(lineItem?.lineItemId)?.totalPriceForRelatedApps ?? 0}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>
              }
            >
              <div className="integrated-apps-line-items-container ">
                {lineItem.appLineItems.map((appLineItem: any) => {
                  return (
                    <div className="line-item-app-container" key={`integrated-apps-${appLineItem?.lineItemId}`}>
                      <div className="line-item-app-icon">
                        <EntitlementIcon
                          logoId={appLineItem?.logoId}
                          productId={appLineItem?.productKey}
                          className="avatar medium pt-4"
                          isCloud
                        />
                      </div>
                      <div className="selected-app">
                        <div className="row-flex-container">
                          <h4 className="selected-app-name integrated-app">{appLineItem?.name}</h4>
                          <Estimate
                            currency={'USD'}
                            price={actualEstimates.get(appLineItem?.lineItemId)?.grandTotal ?? appLineItem?.price ?? 0}
                            loading={loading}
                          />
                        </div>
                        <ProductTrialInfo trialLength={productTrialMap.get(appLineItem?.productKey)} isApp={true} />
                        <div className="row-flex-container">
                          <p className="selected-app-pricing-details">
                            {`Plan: ${appLineItem?.offeringName}`}
                            {!appLineItem?.isFreeProduct ? ', ' + appLineItem?.billingCycleLabel : ''}
                          </p>
                        </div>
                        <div className="row-flex-container">
                          <p className="selected-app-pricing-details">
                            Estimated {getChargeElement({ productId: appLineItem?.productKey, labelPlural: true })}{' '}
                          </p>
                          <p className="selected-app-pricing-details">{appLineItem?.estimatedNumberOfUsers}</p>
                        </div>
                        <div className="row-flex-container">
                          <p className="selected-app-pricing-details">List price </p>
                          <Estimate
                            currency={'USD'}
                            price={actualEstimates.get(appLineItem?.lineItemId)?.listPrice ?? appLineItem?.price ?? 0}
                            loading={loading}
                          />
                        </div>
                        {actualEstimates.get(appLineItem?.lineItemId)?.allPromotionsApplied &&
                          actualEstimates.get(appLineItem?.lineItemId)?.allPromotionsApplied.map((promotionInfo) => {
                            return (
                              <div
                                className="row-flex-container"
                                key={`${appLineItem?.lineItemId}-${promotionInfo.adjustmentLabel}`}
                              >
                                <p className="selected-app-pricing-details">{`${promotionInfo.adjustmentLabel}: ${promotionInfo.percent}%`}</p>
                                <Estimate
                                  currency={'USD'}
                                  price={-(promotionInfo.totalAdjustment ?? 0)}
                                  loading={loading}
                                  className={'sub-title-pricing-currency'}
                                />
                              </div>
                            );
                          })}
                        <div className="row-flex-container">
                          <h4 className="estimate-partner-discount">Estimate incl. discounts</h4>
                          <Estimate
                            currency={'USD'}
                            price={actualEstimates.get(appLineItem?.lineItemId)?.estimateIncludingPartnerDiscount ?? 0}
                            loading={loading}
                            className={'estimate-partner-discount-currency'}
                          />
                        </div>
                        {actualEstimates.get(appLineItem.lineItemId)?.isAdminFeeApplicable && (
                          <div className="row-flex-container">
                            <p className="selected-app-pricing-details">{`Administration fee (DST)`}</p>
                            <Estimate
                              currency={'USD'}
                              price={actualEstimates.get(appLineItem?.lineItemId)?.adminFee ?? 0}
                              loading={loading}
                              className={'sub-title-pricing-currency'}
                            />
                          </div>
                        )}
                        {actualEstimates
                          .get(appLineItem?.lineItemId)
                          ?.itemLevelTaxInfo?.map(([taxLabel, { tax, taxPercent }]) => {
                            return (
                              <div key={`tax-(${taxPercent}-${taxLabel})`} className="row-flex-container">
                                <p className="selected-app-pricing-details">{`Tax (${taxPercent}% ${taxLabel})`}</p>
                                <Estimate
                                  currency={'USD'}
                                  price={tax}
                                  loading={loading}
                                  className={'sub-title-pricing-currency'}
                                />
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>
        )}
      </>
    );
  });
};

export const OrderReviewWithProvisioning: React.FC<OrderReviewWithProvisioningProps> = ({
  lineItems,
  transactionAccountId,
  onClose,
}) => {
  const context = useApplicationContext();

  const partnerAccounts = context.partnerAccounts;
  const relatedPartnerAccount = partnerAccounts?.find(
    (partnerAccount: PartnerAccount) => partnerAccount.transactionAccountId === transactionAccountId
  );
  const {
    setQuoteItems,
    familyContainerId,
    setEstimatesToBeRefreshed,
    existingCloudSite,
    pricingPlanCycle,
    productTrialMap,
    invoiceGroupIdAttachedToExistingSite,
    getAppsParentEntitlement,
    siteCustomerContacts,
  } = usePartnerCatalogueContext();

  const jiraLineItems = lineItems.filter((lineItem) => lineItem.isItAJiraProduct && !lineItem.isApp);
  const nonJiraLineItems = lineItems.filter((lineItem) => !lineItem.isItAJiraProduct && !lineItem.isApp);

  const appLineItemsIndex = jiraLineItems?.findIndex(
    (jiraLineItems) => jiraLineItems?.appLineItems && jiraLineItems?.appLineItems?.length
  );
  if (appLineItemsIndex > -1) {
    // if any jira line-item is having appLineItems, moving that line-item to last index in the array
    // reason for this change is to display integrated apps at the end of all jira products in review order page
    const tempLineItem = jiraLineItems[appLineItemsIndex];
    jiraLineItems.splice(appLineItemsIndex, 1);
    jiraLineItems.push(tempLineItem);
  }

  const appsWithExistingEntitlementLineItems = lineItems.filter((lineItem) => lineItem.isApp);
  const areAllTheAddedProductsFree = lineItems.every((lineItem) => lineItem.isFreeProduct);

  const [selectedInvoiceGroupId, setSelectedInvoiceGroupId] = React.useState(invoiceGroupIdAttachedToExistingSite);
  const [areEstimatesRefreshing, setAreEstimatesRefreshing] = React.useState(false);
  const [selectedSoldToAddress, setSelectedSoldToAddress] = React.useState<CommerceAddress>({});
  const [currentPricingEligibility, setCurrentPricingEligibility] = React.useState<string>('COMMERCIAL');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentMethod>({} as PaymentMethod);
  const [invoiceGroupInfoForExistingSite, setInvoiceGroupInfoForExistingSite] = React.useState<
    generatedTypesNew.InvoiceGroup | undefined | null
  >();

  const [hasOrderBeenPlaced, setHasOrderBeenPlaced] = React.useState(false);

  const [orderSlug, setOrderSlug] = React.useState('');

  const [agreedWithTnC, setAgreedWithTnC] = React.useState(false);

  const [atlassianAccountIds, setAtlassianAccountIds] = React.useState(['']);

  const [atlassianAccountEmails, setAtlassianAccountEmails] = React.useState<Array<string>>(['']);

  const [isEccLoading, setIsEccLoading] = React.useState<boolean>(false);

  const [areOrderDefaultBeingFetched, setAreOrderDefaultBeingFetched] = React.useState(false);

  const [productToPromotionsMap, setProductToPromotionsMap] = React.useState<
    Record<string, string | undefined> | undefined
  >();

  React.useEffect(() => {
    setSelectedInvoiceGroupId(invoiceGroupIdAttachedToExistingSite);
  }, [invoiceGroupIdAttachedToExistingSite]);

  const [siteDetails, setSiteDetails] = React.useState<SiteDetails>();

  const [shouldPlacingOrderBeBlocked, setShouldPlacingOrderBeBlocked] = React.useState(false);

  const [isEnabled] = useFeatureToggle();

  const [isPartnerMarginIncluded, setIsPartnerMarginIncluded] = React.useState<boolean>(false);
  const [typeOfExportSelected, setTypeOfExport] = React.useState<'PDF' | 'JSON' | undefined>();

  const { closeModalDialog, isModalOpen, openModalDialog } = useModalDialog();

  const { notifyFailure } = usePartnerCheckoutNotification();

  const billToSkeletonLoader = (
    <SkeletonLoaderWrapper>
      <SkeletonLoader width="542px" height="156px" marginBottom={'0'} />
    </SkeletonLoaderWrapper>
  );

  const { getPromotions } = usePartnerPromotionDetails(
    transactionAccountId,
    pricingPlanCycle,
    // Passing all the product keys(product and the apps added for the same) for which we have find out the partner promotions
    lineItems.reduce<
      {
        productKey: string;
        isApp: boolean;
      }[]
    >((acc, lineItem) => {
      return [
        ...acc,
        {
          productKey: lineItem.productKey,
          isApp: Boolean(lineItem.isApp),
        },
        ...lineItem.appLineItems.map((appLineItem) => {
          return {
            productKey: appLineItem.productKey,
            isApp: true,
          };
        }),
      ];
    }, [])
  );

  const ansGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const {
    data,
    refetch: refetchAvailableInvoiceGroups,
    error: fetchAvailableInvoiceGroupsError,
    loading: availableInvoiceGroupsLoading,
  } = useQuery<FetchAvailableInvoiceGroupsForOrderReviewQuery, FetchAvailableInvoiceGroupsForOrderReviewQueryVariables>(
    fetchAvailableInvoiceGroupsForOrderReview,
    {
      variables: {
        id: transactionAccountId,
      },
      client: ansGraphqlClient as any,
      skip: areAllTheAddedProductsFree,
    }
  );

  const calculateOrderEstimate = async (pricingEligibility?: string) => {
    let map = productToPromotionsMap;
    try {
      setAreEstimatesRefreshing(true);
      if (!areAllTheAddedProductsFree && map === undefined) {
        map = await getPromotions();
      }
      await getEstimate({
        intentEstimateRequestPayload: {
          transactionAccountId,
          orderIntentId,
          items: processLineItemsForOrderEstimate(lineItems, map, pricingEligibility ?? currentPricingEligibility),
          ...(selectedInvoiceGroupId && { invoiceGroupId: selectedInvoiceGroupId }),
        },
      });
      setAreEstimatesRefreshing(false);
      return;
    } catch (err) {
      notifyFailure(tableErrorMessage(), CheckoutOperation.CalculateEstimate);
      setAreEstimatesRefreshing(false);
      setShouldPlacingOrderBeBlocked(true);
    } finally {
      setProductToPromotionsMap(map);
    }
  };

  React.useEffect(() => {
    (async () => {
      if (data && invoiceGroupIdAttachedToExistingSite) {
        const invoiceGroupInfoForExistingSite = (data?.transactionAccount?.availableInvoiceGroups?.nodes ?? [])?.find(
          (invoiceGroup) => invoiceGroup?.id === invoiceGroupIdAttachedToExistingSite
        );
        setInvoiceGroupInfoForExistingSite(invoiceGroupInfoForExistingSite);
        const pricingEligibility =
          invoiceGroupInfoForExistingSite?.shipToPriceEligibility?.toUpperCase() ?? 'COMMERCIAL';
        if (invoiceGroupIdAttachedToExistingSite) {
          await calculateOrderEstimate(pricingEligibility);
        }
        setCurrentPricingEligibility(pricingEligibility);
      }
    })();
  }, [data]);

  React.useEffect(() => {
    (async () => {
      if (invoiceGroupIdAttachedToExistingSite) {
        const invoiceGroupInfoForExistingSite = (data?.transactionAccount?.availableInvoiceGroups?.nodes ?? []).find(
          (invoiceGroup) => invoiceGroup?.id === invoiceGroupIdAttachedToExistingSite
        );
        setInvoiceGroupInfoForExistingSite(invoiceGroupInfoForExistingSite);
        const pricingEligibility =
          invoiceGroupInfoForExistingSite?.shipToPriceEligibility?.toUpperCase() ?? 'COMMERCIAL';
        if (invoiceGroupIdAttachedToExistingSite) {
          await calculateOrderEstimate(pricingEligibility);
        }
        setCurrentPricingEligibility(pricingEligibility);
      }
    })();
  }, [familyContainerId, invoiceGroupIdAttachedToExistingSite]);

  const availableInvoiceGroups = data?.transactionAccount?.availableInvoiceGroups?.nodes ?? [];

  React.useEffect(() => {
    if (fetchAvailableInvoiceGroupsError) {
      setShouldPlacingOrderBeBlocked(true);
      notifyFailure(tableErrorMessage(), CheckoutOperation.Others);
      return;
    }
    setShouldPlacingOrderBeBlocked(false);
  }, [fetchAvailableInvoiceGroupsError]);

  const { loading: isBillToLoading, data: billToAddressData } = useQuery<
    FetchBillToAddressByTransactionAccountIdQuery,
    FetchBillToAddressByTransactionAccountIdQueryVariables
  >(fetchBillToAddressByTransactionAccountId, {
    variables: { id: transactionAccountId },
    client: (ansGraphqlClient as unknown) as any,
    skip: !transactionAccountId,
  });

  let paymentMethodInCaseExistingSiteSelected = invoiceGroupInfoForExistingSite?.defaultPaymentMethod;
  paymentMethodInCaseExistingSiteSelected = paymentMethodInCaseExistingSiteSelected
    ? (formatIfCreditCard(paymentMethodInCaseExistingSiteSelected) as any)
    : undefined;
  const shipToInCaseExistingSiteSelected = invoiceGroupInfoForExistingSite?.shipToParty;

  const { isEstimatePDFDownloading, downloadOrderEstimatePDF } = useOrderEstimatePDFDownload();

  const {
    processLineItemsForOrderEstimate,
    getEstimate,
    orderIntentId,
    actualEstimates,
    totalPartnerDiscountApplied,
    totalAmountToPay,
    totalTaxPayable,
    totalAmountAfterDiscountAndAdminFeeButBeforeTaxes,
    orderCreationItems,
    taxBreakUp,
    adminTaxBreakUp,
    resetAllLineItemValues,
    getOrderDefaults,
  } = useProcessLineItemsForOrderIntentEstimate({
    lineItemsData: lineItems,
  });

  const totalEstimate = lineItems.reduce((acc, lineItem) => {
    return acc + (lineItem.estimatedPrice ?? 0);
  }, 0);

  React.useEffect(() => {
    (async () => {
      try {
        if (selectedSoldToAddress?.id && selectedPaymentMethod?.id) {
          const invoiceGroupInfo = await getInvoiceGroup({
            availableInvoiceGroups: availableInvoiceGroups as any,
            selectedPaymentMethod,
            transactionAccountId,
            selectedSoldToAddress,
          });
          if (invoiceGroupInfo) {
            const { applicableInvoiceGroupId, isRefetchRequired } = invoiceGroupInfo;
            setSelectedInvoiceGroupId(applicableInvoiceGroupId);
            if (isRefetchRequired) {
              await refetchAvailableInvoiceGroups();
            }
          }
        }
      } catch {
        notifyFailure(tableErrorMessage(), CheckoutOperation.Others);
      }
    })();
  }, [selectedSoldToAddress, selectedPaymentMethod]);

  // Start - This block concerns when existing site has not been selected
  // This following effect will load the estimate for the first time the Order Summary drawer opens
  React.useEffect(() => {
    (async () => {
      if (!invoiceGroupIdAttachedToExistingSite) {
        await calculateOrderEstimate();
      }
    })();
  }, [selectedInvoiceGroupId]);
  // End - This block concerns when existing site has not been selected

  React.useEffect(() => {
    if (siteCustomerContacts.loading) {
      setIsEccLoading(true);
    } else {
      const accountIds =
        siteCustomerContacts.data.length > 0 ? siteCustomerContacts.data.map(({ account_id }) => account_id) : [''];
      const accountEmails =
        siteCustomerContacts.data.length > 0 ? siteCustomerContacts.data.map(({ name }) => name) : [''];
      setAtlassianAccountIds(accountIds);
      setAtlassianAccountEmails(accountEmails);
      setIsEccLoading(false);
    }
  }, [JSON.stringify(siteCustomerContacts)]);

  const actualEstimatesAvailable = actualEstimates.size > 0;
  const { orderBeingPlaced, placeOrder, orderId, processOrderCreationItemsForOrder } = usePlaceOrder();
  const { updateCustomerConsent } = useConsentHub();

  const createOrderPlacementEvent = (lineItems: PartnerQuoteLineItems) => {
    const selectedProducts = [];
    const selectedApps = [];
    lineItems?.forEach((element) => {
      if (element.isApp) {
        selectedApps.push({ name: element.name });
      } else {
        selectedProducts.push({ name: element.name });
      }
    });
  };

  const handleSubmit = async () => {
    if (orderCreationItems) {
      try {
        let nextBillingAnchorTimestamp: number | undefined = undefined;
        let invoiceGroupIdForOrder = selectedInvoiceGroupId;
        //if order has JIRA products and also there are JIRA products in the existing site, then only we need to call defaults API
        if (jiraLineItems.length > 0 && familyContainerId) {
          setAreOrderDefaultBeingFetched(true);
          const anyJiraOfferingId = jiraLineItems[0]?.offeringKey;
          const defaults = await getOrderDefaults({
            offeringId: anyJiraOfferingId,
            transactionAccountId,
          });
          nextBillingAnchorTimestamp = defaults.billingAnchorTime?.value;
          invoiceGroupIdForOrder = defaults.invoiceGroupId?.value ?? '';
        }
        const placeOrderParams: PlaceOrderParams = {
          transactionAccountId,
          orderIntentId,
          items: processOrderCreationItemsForOrder(lineItems, orderCreationItems, nextBillingAnchorTimestamp),
          endCustomerContacts: atlassianAccountIds,
          onOrderPlaced: async (orderResponse) => {
            setEstimatesToBeRefreshed(true);
            resetAllLineItemValues();
            setQuoteItems([]);
            setAreOrderDefaultBeingFetched(false);
            setHasOrderBeenPlaced(true);
            setOrderSlug(orderResponse?.slug ?? '');
            createOrderPlacementEvent(lineItems);
          },
        };
        if (!areAllTheAddedProductsFree) {
          if (invoiceGroupIdForOrder === '') {
            throw new Error(
              'Either sold-to address or payment method has not been selected. Both must be selected to place an order.'
            );
          }
          placeOrderParams.invoiceGroupId = invoiceGroupIdForOrder;
          if (siteDetails?.siteId) {
            placeOrderParams.siteId = siteDetails.siteId;
          }
        }
        await updateCustomerConsent();
        await placeOrder(placeOrderParams);
      } catch (err: any) {
        notifyFailure(
          err.message ??
            'Something is not right at our end. Please try again. If the issue persists, please contact us.',
          CheckoutOperation.PlaceOrder
        );
      }
    }
  };

  const handleEstimatePDFDownload = async () => {
    await downloadOrderEstimatePDF({
      ...(selectedInvoiceGroupId && { invoiceGroupId: selectedInvoiceGroupId }),
      transactionAccountId,

      items: isPartnerMarginIncluded
        ? processLineItemsForOrderEstimate(lineItems, productToPromotionsMap)
        : processLineItemsForOrderEstimate(lineItems, undefined),

      orderIntentId: generateUuid(),
    });
    setIsPartnerMarginIncluded(false);
  };

  const { prepareAndSaveJson, isEstimateJsonIsDownloading } = useEstimateJsonDownload();

  const handleEstimateJSONDownload = async () => {
    await prepareAndSaveJson({
      intentEstimateRequestPayload: {
        transactionAccountId,
        orderIntentId,

        items: isPartnerMarginIncluded
          ? processLineItemsForOrderEstimate(lineItems, productToPromotionsMap)
          : processLineItemsForOrderEstimate(lineItems, undefined),

        ...(selectedInvoiceGroupId && { invoiceGroupId: selectedInvoiceGroupId }),
      },
    });
  };
  const isAllEndCustomerContactAdded = React.useMemo(() => !(atlassianAccountIds.findIndex((id) => !id) >= 0), [
    atlassianAccountIds,
  ]);

  const renderPartnerMarginModal = () => {
    const partnerDiscountsText = DISCOUNT_LABELS.PARTNER_DISCOUNT.toLowerCase() + 's';

    return (
      <ModalTransition>
        {isModalOpen && (
          <Modal onClose={closeModalDialog} testId="partner-margin-dialog-content">
            <ModalHeader>
              <ModalTitle>{`Include ${partnerDiscountsText}?`}</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {`By default, the estimate PDF/JSON doesn't include ${partnerDiscountsText}. Select the checkbox to include them.`}
              <div className="partner-margin-checkbox">
                <Checkbox
                  label={`Include ${partnerDiscountsText}`}
                  isChecked={isPartnerMarginIncluded}
                  onChange={() => {
                    setIsPartnerMarginIncluded((prevValue) => !prevValue);
                  }}
                  testId="partner-margin-checkbox"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closeModalDialog}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  typeOfExportSelected === 'PDF' ? handleEstimatePDFDownload() : handleEstimateJSONDownload();
                  closeModalDialog();
                }}
                testId="partner-margin-dialog-continue-btn"
              >
                Continue
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    );
  };

  const getTotalMonthlyAmountForConsentHub = () => {
    return (
      <Estimate
        currency={'USD'}
        price={totalAmountToPay ?? totalEstimate}
        isTotal={true}
        loading={areEstimatesRefreshing}
        className={'order-info'}
        // This is a very niche case where we need to show this text bold, so no need of a class
        style={{
          fontWeight: 'bold',
        }}
      />
    );
  };

  return (
    <div className="order-review-provisioning-container" data-testid="ppc-order-review-provisioning">
      {renderPartnerMarginModal()}
      {hasOrderBeenPlaced ? (
        // If existing site has been selected, then Get started button won't be shown
        siteDetails ? (
          <OrderConfirmationStep orderSlug={orderSlug} orderId={orderId} transactionAccountId={transactionAccountId} />
        ) : (
          <OrderConfirmationStep
            orderSlug={orderSlug}
            orderId={orderId}
            transactionAccountId={transactionAccountId}
            showGetStartedAction={true}
            onGetStarted={(link: string) => {
              window.open(link);
            }}
          />
        )
      ) : (
        <>
          <div className="order-summary-header">
            <h1>Order summary</h1>
            <p className="order-summary-sub-header">
              Review the subscription and billing details before confirming your order.
            </p>
          </div>
          <div className="order-review-summary">
            <div className="order-review-details">
              <div className="line-items-container">
                <div className="line-items">
                  <>
                    {renderAppsWithParentEntitlementLineItems({
                      lineItems: appsWithExistingEntitlementLineItems,
                      actualEstimates,
                      loading: areEstimatesRefreshing || availableInvoiceGroupsLoading,
                      adminTaxBreakUp,
                      productTrialMap,
                      getAppsParentEntitlement,
                    })}

                    {jiraLineItems && jiraLineItems.length > 0 && appsWithExistingEntitlementLineItems.length > 0 && (
                      <Divider />
                    )}
                    {renderLineItems({
                      lineItems: jiraLineItems,
                      isJiraFamily: true,
                      actualEstimates,
                      actualEstimatesAvailable,
                      loading: areEstimatesRefreshing,
                      existingCloudSite,
                      adminTaxBreakUp,
                      productTrialMap,
                    })}
                    {jiraLineItems && jiraLineItems.length > 0 && nonJiraLineItems.length > 0 && <Divider />}
                    {renderLineItems({
                      lineItems: nonJiraLineItems,
                      isJiraFamily: false,
                      actualEstimates,
                      actualEstimatesAvailable,
                      loading: areEstimatesRefreshing,
                      existingCloudSite: undefined,
                      adminTaxBreakUp,
                      productTrialMap,
                    })}
                  </>
                </div>
                <div className="total-estimate-summary">
                  <div className="row-flex-container">
                    <h5 className="total-estimate-label">Total excl. tax</h5>
                    <Estimate
                      currency={'USD'}
                      price={totalAmountAfterDiscountAndAdminFeeButBeforeTaxes ?? totalEstimate}
                      isTotal={true}
                      loading={areEstimatesRefreshing}
                      className={'total-estimate-currency'}
                    />
                  </div>
                  {!areAllTheAddedProductsFree && (
                    <div className="row-flex-container">
                      <div className="row-flex-container gap">
                        <p className="selected-app-pricing-details">Tax estimate</p>
                        <ToolTip
                          position="top"
                          content="Tax estimate will be shown once sold-to address and payment method have been selected."
                        >
                          <InfoIcon size={'small'} label={'Tax estimate'} />
                        </ToolTip>
                      </div>
                      <p className="selected-app-pricing-details"></p>
                      <div className="row-flex-container gap">
                        {totalTaxPayable > 0 && (
                          <ToolTip position="top-end" content={renderTaxBreakUp(taxBreakUp)}>
                            <InfoIcon size={'small'} label={'Tax breakup'} />
                          </ToolTip>
                        )}
                        <Estimate
                          currency={'USD'}
                          price={totalTaxPayable}
                          className={'sub-title-pricing-currency'}
                          loading={areEstimatesRefreshing}
                        />
                      </div>
                    </div>
                  )}
                  <div className="row-flex-container">
                    <p className="selected-app-pricing-details">Partner discount estimate</p>
                    <p className="selected-app-pricing-details"></p>
                    <Estimate
                      currency={'USD'}
                      price={-(totalPartnerDiscountApplied ?? 0)}
                      className={'sub-title-pricing-currency'}
                      loading={areEstimatesRefreshing}
                    />
                  </div>
                  <Divider className="mt-16 mb-16" />
                  <div className="row-flex-container">
                    <h5 className="grand-total-estimate">Total monthly estimate</h5>
                    <Estimate
                      currency={'USD'}
                      price={totalAmountToPay ?? totalEstimate}
                      isTotal={true}
                      loading={areEstimatesRefreshing}
                      className={'grand-total-currency'}
                    />
                  </div>
                  <div className="row-flex-container">
                    <p>
                      Actual amount charged may be different if the number of users has changed by the end of the trial
                      period.
                    </p>
                  </div>
                </div>
              </div>
              <div className="place-order-actions-container">
                <Form onSubmit={handleSubmit}>
                  {({ formProps }) => {
                    return (
                      <form {...formProps}>
                        <div className="place-order-actions">
                          {!areAllTheAddedProductsFree && (
                            <div className={`order-info-selects`} data-testid="ppc-order-info-selects-container">
                              <div className="commerce-address-container">
                                <div className="selected-commerce-address-container">
                                  <FormatCommerceAddress
                                    commerceAddress={{
                                      addressLine1: relatedPartnerAccount?.billTo?.street,
                                      city: relatedPartnerAccount?.billTo?.city,
                                      country: relatedPartnerAccount?.billTo?.country,
                                      state: relatedPartnerAccount?.billTo?.state,
                                      postcode: relatedPartnerAccount?.billTo?.postalCode,
                                      taxId: relatedPartnerAccount?.taxId,
                                      name: relatedPartnerAccount?.name,
                                    }}
                                  />
                                </div>
                              </div>
                              <AddExistingSite
                                transactionAccountId={transactionAccountId}
                                lineItems={lineItems}
                                onExistingSiteAdded={({ siteDetails, isExistingSiteValidForProvisioning }) => {
                                  setSiteDetails(siteDetails);
                                  setShouldPlacingOrderBeBlocked(!isExistingSiteValidForProvisioning);
                                }}
                              />
                              {availableInvoiceGroupsLoading && invoiceGroupIdAttachedToExistingSite ? (
                                billToSkeletonLoader
                              ) : (
                                <SearchSoldToAddress
                                  transactionAccountId={transactionAccountId}
                                  onSelectSoldToAddress={(soldToAddress) => {
                                    setSelectedSoldToAddress(soldToAddress);
                                    setCurrentPricingEligibility(
                                      soldToAddress.priceEligibility?.toUpperCase() ?? 'COMMERCIAL'
                                    );
                                  }}
                                  {...(invoiceGroupIdAttachedToExistingSite && {
                                    autoSelectedSoldToAddress: shipToInCaseExistingSiteSelected,
                                    skipLoadingPaymentMethods: true,
                                  })}
                                />
                              )}
                              {availableInvoiceGroupsLoading && invoiceGroupIdAttachedToExistingSite ? (
                                billToSkeletonLoader
                              ) : (
                                <SelectPaymentMethod
                                  transactionAccountId={transactionAccountId}
                                  onSelectPaymentMethod={(selectedPaymentMethod) => {
                                    setSelectedPaymentMethod(selectedPaymentMethod);
                                  }}
                                  {...(invoiceGroupIdAttachedToExistingSite && {
                                    autoSelectedPaymentMethod: paymentMethodInCaseExistingSiteSelected as PaymentMethod,
                                    skipLoadingPaymentMethods: true,
                                  })}
                                />
                              )}
                            </div>
                          )}
                          <div className="add-customer-contact-order-flow">
                            <Field label="Customer contact" name="customer-contact" isRequired>
                              {() =>
                                isEccLoading ? (
                                  <SkeletonLoaderWrapper testId="order-review-ecc-loading">
                                    <SkeletonLoader height="40px" />
                                  </SkeletonLoaderWrapper>
                                ) : (
                                  <AddContactFields
                                    setAtlassianAccountIds={setAtlassianAccountIds}
                                    atlassianAccountIds={atlassianAccountIds}
                                    atlassianAccountEmails={atlassianAccountEmails}
                                    setAtlassianAccountEmails={setAtlassianAccountEmails}
                                    emailFieldText="Enter contact email"
                                    nameFieldText="Enter contact full name"
                                    addButtonText="Add customer contact"
                                  />
                                )
                              }
                            </Field>
                          </div>
                          <div className="actions-container">
                            {!areAllTheAddedProductsFree && (
                              <>
                                <p className="order-info">
                                  By selecting 'Confirm' below, you acknowledge that your order contains{' '}
                                  <strong>monthly</strong> auto-renewal subscriptions.{' '}
                                  <strong>
                                    After any no-cost trial periods, You'll be automatically billed the price estimate
                                    of {getTotalMonthlyAmountForConsentHub()} per month (plus tax, if applicable) until
                                    you cancel the subscription at any time.
                                  </strong>{' '}
                                  Our prices may change upon notice to you. To cancel,
                                  <a
                                    data-testid="atlassian-support-link"
                                    href={getPartnerServiceDeskUrlForGenericHelp()}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={trackPartnerServiceDeskClick}
                                  >
                                    {' '}
                                    contact us
                                  </a>
                                  .
                                </p>
                                <p className="order-info">
                                  Estimate and final amount charged includes discounts, taxes, and credits that may{' '}
                                  apply. You'll receive an invoice with the final amount when the trial period ends. You
                                  may first be charged a prorated amount to align your bill date with your other
                                  subscriptions.
                                </p>
                              </>
                            )}
                            <CheckboxField name="ppc-order-review-tnc" isRequired={true}>
                              {({ fieldProps }) => (
                                <Checkbox
                                  {...fieldProps}
                                  label={
                                    <span>
                                      I agree to the{' '}
                                      <a href={legalLinks.termsOfUseUrl}>Atlassian Cloud Terms of Service</a> and
                                      acknowledge the{' '}
                                      <a href={legalLinks.privacyPolicyUrl}>Atlassian Privacy Policy.</a>
                                    </span>
                                  }
                                  isChecked={agreedWithTnC}
                                  onChange={() => {
                                    setAgreedWithTnC((prevState) => {
                                      return !prevState;
                                    });
                                  }}
                                />
                              )}
                            </CheckboxField>
                            <div className="actions-footer">
                              {areAllTheAddedProductsFree ? (
                                <LoadingButton
                                  type="submit"
                                  appearance="primary"
                                  isLoading={orderBeingPlaced || areOrderDefaultBeingFetched}
                                  isDisabled={!agreedWithTnC || orderBeingPlaced}
                                  testId={'ppc-order-review.place-order'}
                                  aria-label="Confirm order"
                                >
                                  Confirm
                                </LoadingButton>
                              ) : (
                                <LoadingButton
                                  type="submit"
                                  appearance="primary"
                                  isLoading={orderBeingPlaced}
                                  isDisabled={
                                    orderBeingPlaced ||
                                    areEstimatesRefreshing ||
                                    !selectedInvoiceGroupId ||
                                    actualEstimates.size === 0 ||
                                    !agreedWithTnC ||
                                    !isAllEndCustomerContactAdded ||
                                    shouldPlacingOrderBeBlocked
                                  }
                                  testId={'ppc-order-review.place-order'}
                                  aria-label="Confirm order"
                                >
                                  Confirm
                                </LoadingButton>
                              )}
                              {
                                <div className="generate-estimate-container">
                                  <DropdownMenu
                                    isLoading={isEstimatePDFDownloading || isEstimateJsonIsDownloading}
                                    trigger="Generate estimate"
                                    triggerType={'button'}
                                    shouldFitContainer={true}
                                    triggerButtonProps={{
                                      shouldFitContainer: true,
                                      isLoading: isEstimatePDFDownloading || isEstimateJsonIsDownloading,
                                      isDisabled: areEstimatesRefreshing || actualEstimates.size === 0,
                                    }}
                                    testId="ppc-order-review-generate-estimate-export"
                                  >
                                    <DropdownItemGroup>
                                      <DropdownItem
                                        onClick={() => {
                                          setTypeOfExport('PDF');
                                          openModalDialog();
                                        }}
                                        testId="ppc-order-review-generate-estimate-export-pdf"
                                      >
                                        PDF
                                      </DropdownItem>
                                      <DropdownItem
                                        onClick={() => {
                                          setTypeOfExport('JSON');
                                          openModalDialog();
                                        }}
                                        testId="ppc-order-review-generate-estimate-export-json"
                                      >
                                        JSON
                                      </DropdownItem>
                                    </DropdownItemGroup>
                                  </DropdownMenu>
                                </div>
                              }
                              <Button appearance="subtle" onClick={onClose}>
                                Back
                              </Button>
                            </div>
                          </div>
                        </div>
                      </form>
                    );
                  }}
                </Form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
